/**
 * Room Auto-Assignment Utility
 * 
 * Автоматическое назначение оптимального номера при создании брони.
 * Алгоритм приоритизации:
 * 1. Номера с минимальным "разрывом" между бронями (максимизация загрузки)
 * 2. При равных условиях — по номеру комнаты (меньший номер)
 * 
 * Это первый шаг к паритету с Oracle OPERA и Mews.
 */

import { supabase } from '@/integrations/supabase/client';

interface Room {
  id: string;
  name: string;
  room_number: string;
  room_category_id: string | null;
  is_available: boolean;
  is_published: boolean;
  capacity: number;
  sort_order: number;
}

interface BookingSlot {
  room_id: string;
  check_in_date: string;
  check_out_date: string;
}

interface AssignmentResult {
  room: Room | null;
  reason: string;
  score: number;
  alternatives: Room[];
}

/**
 * Находит оптимальный номер для брони на основе:
 * 1. Доступности на указанные даты
 * 2. Минимизации "разрывов" между бронями
 * 3. Приоритета по sort_order/room_number
 */
export async function findOptimalRoom(
  hotelId: string,
  categoryId: string,
  checkInDate: string,
  checkOutDate: string,
  requiredCapacity: number = 1,
  excludeRoomId?: string
): Promise<AssignmentResult> {
  try {
    // 1. Получаем все активные номера в категории
    const { data: roomsData, error: roomsError } = await supabase
      .from('rooms')
      .select('id, name, room_number, room_category_id, is_available, is_published, capacity, sort_order')
      .eq('hotel_id', hotelId)
      .eq('room_category_id', categoryId)
      .eq('is_available', true)
      .eq('is_published', true)
      .is('deleted_at', null)
      .gte('capacity', requiredCapacity)
      .order('sort_order', { ascending: true });

    if (roomsError) {
      console.error('[RoomAutoAssignment] Error fetching rooms:', roomsError);
      return { room: null, reason: 'Ошибка загрузки номеров', score: 0, alternatives: [] };
    }

    const rooms = (roomsData || []) as Room[];
    
    if (rooms.length === 0) {
      return { room: null, reason: 'Нет доступных номеров в категории', score: 0, alternatives: [] };
    }

    // Исключаем указанный номер (если перемещаем бронь)
    const availableRooms = excludeRoomId 
      ? rooms.filter(r => r.id !== excludeRoomId)
      : rooms;

    if (availableRooms.length === 0) {
      return { room: null, reason: 'Все номера заняты', score: 0, alternatives: [] };
    }

    // 2. AK-47: занятость берём с сервера — та же функция, что и триггер защиты
    //    (учитывает буфер уборки отеля из /admin/rooms → «Заезд/Выезд»)
    const { data: availabilityData, error: availabilityError } = await supabase.rpc(
      'get_room_availability_for_move',
      {
        p_hotel_id: hotelId,
        p_category_id: categoryId,
        p_check_in: checkInDate,
        p_check_out: checkOutDate,
        p_check_in_time: '14:00',
        p_check_out_time: '12:00',
        p_exclude_booking_id: null,
      } as any
    );

    if (availabilityError) {
      console.error('[RoomAutoAssignment] Server availability failed:', availabilityError);
      return {
        room: null,
        reason: 'Проверка занятости недоступна — выберите номер вручную',
        score: 0,
        alternatives: [],
      };
    }

    const serverAvailability: Record<string, boolean> = {};
    ((availabilityData as any[]) || []).forEach((row) => {
      serverAvailability[row.room_id] = Boolean(row.is_available);
    });

    // 3. Существующие брони нужны только для скоринга «минимизация разрывов»
    // Расширяем период на ±7 дней для расчёта "разрывов"
    const extendedCheckIn = new Date(checkInDate);
    extendedCheckIn.setDate(extendedCheckIn.getDate() - 7);
    const extendedCheckOut = new Date(checkOutDate);
    extendedCheckOut.setDate(extendedCheckOut.getDate() + 7);

    const roomIds = availableRooms.map(r => r.id);

    const { data: existingBookings, error: bookingsError } = await supabase
      .from('booking_core')
      .select('room_id, check_in_date, check_out_date')
      .in('room_id', roomIds)
      .is('deleted_at', null)
      .not('status', 'eq', 'cancelled')
      .gte('check_out_date', extendedCheckIn.toISOString().split('T')[0])
      .lte('check_in_date', extendedCheckOut.toISOString().split('T')[0]);

    if (bookingsError) {
      console.error('[RoomAutoAssignment] Error fetching bookings:', bookingsError);
    }

    const bookings = (existingBookings || []) as BookingSlot[];

    // 3. Проверяем доступность каждого номера и рассчитываем "score"
    const scoredRooms: Array<{ room: Room; score: number; available: boolean }> = [];

    for (const room of availableRooms) {
      const roomBookings = bookings.filter(b => b.room_id === room.id);

      // Вердикт сервера — единственный источник правды.
      // Номера, которых нет в ответе сервера, считаем занятыми (не угадываем).
      if (serverAvailability[room.id] !== true) {
        scoredRooms.push({ room, score: -1, available: false });
        continue;
      }

      // 4. Рассчитываем score на основе минимизации разрывов
      let score = 100; // Базовый score

      // Находим ближайшую бронь ДО нашей даты заезда
      const bookingsBefore = roomBookings
        .filter(b => new Date(b.check_out_date) <= new Date(checkInDate))
        .sort((a, b) => new Date(b.check_out_date).getTime() - new Date(a.check_out_date).getTime());

      if (bookingsBefore.length > 0) {
        const lastCheckOut = new Date(bookingsBefore[0].check_out_date);
        const newCheckIn = new Date(checkInDate);
        const gapDaysBefore = Math.floor((newCheckIn.getTime() - lastCheckOut.getTime()) / (1000 * 60 * 60 * 24));
        
        // Чем меньше разрыв — тем выше score (бонус до 50 очков)
        if (gapDaysBefore === 0) score += 50; // Идеально — без разрыва
        else if (gapDaysBefore === 1) score += 40;
        else if (gapDaysBefore === 2) score += 30;
        else if (gapDaysBefore <= 7) score += 20;
      }

      // Находим ближайшую бронь ПОСЛЕ нашей даты выезда
      const bookingsAfter = roomBookings
        .filter(b => new Date(b.check_in_date) >= new Date(checkOutDate))
        .sort((a, b) => new Date(a.check_in_date).getTime() - new Date(b.check_in_date).getTime());

      if (bookingsAfter.length > 0) {
        const nextCheckIn = new Date(bookingsAfter[0].check_in_date);
        const newCheckOut = new Date(checkOutDate);
        const gapDaysAfter = Math.floor((nextCheckIn.getTime() - newCheckOut.getTime()) / (1000 * 60 * 60 * 24));
        
        // Чем меньше разрыв — тем выше score (бонус до 50 очков)
        if (gapDaysAfter === 0) score += 50;
        else if (gapDaysAfter === 1) score += 40;
        else if (gapDaysAfter === 2) score += 30;
        else if (gapDaysAfter <= 7) score += 20;
      }

      // Приоритет по sort_order (меньший номер = чуть выше score)
      score += (100 - room.sort_order) * 0.1;

      scoredRooms.push({ room, score, available: true });
    }

    // 5. Сортируем по score (от большего к меньшему)
    const availableScoredRooms = scoredRooms
      .filter(sr => sr.available)
      .sort((a, b) => b.score - a.score);

    if (availableScoredRooms.length === 0) {
      const unavailableRooms = scoredRooms.filter(sr => !sr.available).map(sr => sr.room);
      return { 
        room: null, 
        reason: `Все ${rooms.length} номеров заняты на эти даты`, 
        score: 0, 
        alternatives: unavailableRooms 
      };
    }

    const bestRoom = availableScoredRooms[0];
    const alternatives = availableScoredRooms.slice(1, 4).map(sr => sr.room);

    console.log('[RoomAutoAssignment] Optimal room found:', {
      room: bestRoom.room.name,
      score: bestRoom.score,
      alternatives: alternatives.map(r => r.name)
    });

    return {
      room: bestRoom.room,
      reason: `Оптимальный выбор (score: ${bestRoom.score.toFixed(0)})`,
      score: bestRoom.score,
      alternatives
    };

  } catch (error) {
    console.error('[RoomAutoAssignment] Unexpected error:', error);
    return { room: null, reason: 'Неожиданная ошибка', score: 0, alternatives: [] };
  }
}

/**
 * Проверяет доступность конкретного номера на указанные даты
 */
export async function isRoomAvailable(
  roomId: string,
  checkInDate: string,
  checkOutDate: string,
  excludeBookingId?: string
): Promise<{ available: boolean; conflictingBookings: string[] }> {
  try {
    let query = supabase
      .from('booking_core')
      .select('id, booking_reference, check_in_date, check_out_date')
      .eq('room_id', roomId)
      .is('deleted_at', null)
      .not('status', 'eq', 'cancelled')
      .lt('check_in_date', checkOutDate)
      .gt('check_out_date', checkInDate);

    if (excludeBookingId) {
      query = query.neq('id', excludeBookingId);
    }

    const { data: conflicts, error } = await query;

    if (error) {
      console.error('[RoomAutoAssignment] Error checking availability:', error);
      return { available: false, conflictingBookings: [] };
    }

    return {
      available: (conflicts || []).length === 0,
      conflictingBookings: (conflicts || []).map(c => c.booking_reference || c.id)
    };
  } catch (error) {
    console.error('[RoomAutoAssignment] Unexpected error:', error);
    return { available: false, conflictingBookings: [] };
  }
}

/**
 * Получает список всех свободных номеров в категории на указанные даты
 */
export async function getAvailableRooms(
  hotelId: string,
  categoryId: string,
  checkInDate: string,
  checkOutDate: string,
  requiredCapacity: number = 1
): Promise<Room[]> {
  const result = await findOptimalRoom(hotelId, categoryId, checkInDate, checkOutDate, requiredCapacity);
  
  if (result.room) {
    return [result.room, ...result.alternatives];
  }
  
  return [];
}
